import React from "react";
import styled from "styled-components";
import { colors } from "src/theme/colors";
import { line, radii, shadow } from "src/theme/ui";

/**
 * The bubble every chart in this product reads through.
 *
 * It was written inside `PieRing`, so the ring had one and the radar beside
 * it had whatever its library drew by default — a white box with a bullet, a
 * colon and a raw field name. One bubble, one anatomy: a dot in the series'
 * own colour, the name in muted ink, the figure at 600 in ink and tabular so
 * two readings a hover apart line up.
 *
 * It is deliberately not a card: 8px corners, a hairline and the dismissible
 * shadow, because a tooltip is the one thing on a page that is genuinely
 * floating above it.
 */
const Bubble = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border: 1px solid ${line.hairline};
  border-radius: ${radii.control}px;
  background: ${colors.white};
  box-shadow: ${shadow.pop};
  font-size: 12px;
  line-height: 1.2;
  white-space: nowrap;
`;

const Dot = styled.span<{ $color: string }>`
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const Name = styled.span`
  color: ${colors.gray[600]};
`;

const Value = styled.b`
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${colors.gray[900]};
`;

export type ChartTipProps = {
  /** The series' or arc's own colour, as the leading dot. */
  color?: string;
  name: React.ReactNode;
  value: React.ReactNode;
};

const ChartTip: React.FC<ChartTipProps> = ({ color, name, value }) => (
  <Bubble>
    {color && <Dot $color={color} />}
    <Name>{name}</Name>
    <Value>{value}</Value>
  </Bubble>
);

export default ChartTip;
